import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext'; 
import { PostSection } from './GalleryUIMobileMusicdetail';
import { useTranslation } from 'react-i18next';


export type MediaEntry = {
  kind: 'image' | 'video';
  // image
  url?: string;
  // new video pipeline
  isHls?: boolean;
  videoId?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // music
  musicTitle?: string | null;
  musicArtist?: string | null;
  musicId?: string | null;
};

type Usage = {
  postId: string;
  url: string;
  musicTitle: string | null;
  musicArtist: string | null;
  filterId: string | null;
  createdAt: string;
  userId?: string;
  ownerId?: string;
  userDetails?: {  _id: string; profilename?: string; picture?: string | null; } | null;
  location?: { city?: string; country?: string; countryCode?: string;};
  likes?: number;
  likedByUser?: boolean;
  media?: MediaEntry[]; 
};

type Track = {
  _id: string;
  title: string;
  artist?: string | null;
  duration?: number | null;
  coverUrl?: string | null;
  audioUrl?: string | null;
};

export interface Post {
  _id: string;
  media: MediaEntry[];  
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: { city: string; country: string; countryCode?: string; };
  user: {  _id: string; profilename: string; picture?: string | null; } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
}

export default function MusicDetailPosts() {
  const { trackId, postId } = useParams<{
    trackId: string;
    postId: string;
  }>();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { likedIds, likePost, unlikePost } = useLikes();

  const [track, setTrack] = useState<Track | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [likingInProgress, setLikingInProgress] = useState<string | null>(null);

  const globalMuted = true;

  // When tapping a music pill (for media without musicId), we can navigate to this track
  const handleMusicClick = (entry: MediaEntry) => {
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
    } else if (track?._id) {
      navigate(`/musicdetails/${track._id}`);
    }
  };

  
  // Fetch track and post data
  useEffect(() => {
  if (!trackId || !postId) return;

  (async () => {
    setLoading(true);
    try {
      const res = await api.get(`/music/${trackId}/usages-with-posts`);

      const trackData: Track | null = res.data?.track ?? null;
      const usagesRaw: Usage[] = Array.isArray(res.data?.usages) ? res.data.usages : [];

      setTrack(trackData);

      const postUsages = usagesRaw.filter((u) => u.postId === postId);

      if (!postUsages.length) {
        setPost(null);
        return;
      }

      const first = postUsages[0];

const media: MediaEntry[] = postUsages.flatMap((u) => {
  if (!Array.isArray(u.media)) return [];

  return u.media.map((m) => ({
    kind: m.kind,
    url: m.url,

    isHls: m.isHls,
    videoId: m.videoId ?? null,
    hlsUrl: m.hlsUrl ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? null,

    musicId: m.musicId ?? null,
    musicTitle: m.musicTitle ?? null,
    musicArtist: m.musicArtist ?? null,
  }));
});

      const loc = (first as any).location || {};
      const likes = typeof first.likes === 'number' ? first.likes : 0;
      const likeKey = likeKeyFromRow({ ...first, postId: first.postId });

 const mapped: Post = {
  _id: first.postId,
  likeKey,
  media,
  captions: [],
  createdAt: first.createdAt || new Date().toISOString(),
  likes,
  totalLikes: likes,
  likedByUser: likedIds.has(likeKey),
  user: first.userDetails ? {
    _id: first.userDetails._id,
    profilename: first.userDetails.profilename || t('gallery.post.unknownUser'),
    picture: first.userDetails.picture ?? null,
  } : null,
  location: {
    city: loc.city || t('gallery.unknownLocation'),
    country: loc.country || t('gallery.unknownLocation'),
    countryCode: loc.countryCode,
  },
  hashtags: [],
  status: undefined,
};

      setPost(mapped);
    } catch (e) {
      console.error('Failed to load music usages for single post:', e);
      alert(t('musicDetail.loadError'));
    } finally {
      setLoading(false);
    }
  })();
}, [trackId, postId, t, likedIds]);

  const handleLike = async (id: string) => {
    const key = String(post?.likeKey || id);
    if (likingInProgress === key || likedIds.has(key)) return;

    setLikingInProgress(key);
    try {
      const newLikes = await likePost(key);
      setPost((prev) => prev ? {
        ...prev,
        likedByUser: true,
        likes: typeof newLikes === 'number' ? newLikes : prev.likes + 1,
        totalLikes: typeof newLikes === 'number' ? newLikes : (prev.totalLikes ?? prev.likes) + 1,
      } : prev);
    } finally {
      setLikingInProgress(null);
    }
  };

  const handleUnlike = async (id: string) => {
    const key = String(post?.likeKey || id);
    if (likingInProgress === key || !likedIds.has(key)) return;

    setLikingInProgress(key);
    try {
      const newLikes = await unlikePost(key);
      setPost((prev) => prev ? {
        ...prev,
        likedByUser: false,
        likes: typeof newLikes === 'number' ? newLikes : Math.max(0, prev.likes - 1),
        totalLikes: typeof newLikes === 'number' ? newLikes : Math.max(0, (prev.totalLikes ?? prev.likes) - 1),
      } : prev);
    } finally {
      setLikingInProgress(null);
    }
  };


  // UI Render
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000',
        color: '#e6e9f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {t('musicDetail.loading')}
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000',
        color: '#e6e9f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
      }}>
        {t('musicDetail.notFound')}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      background: '#000',
      position: 'relative',
    }}>
      {/* Close button */}
      <button
        onClick={() => navigate(-1)}
        aria-label={t('musicDetail.close')}
        title={t('musicDetail.close')}
        style={closeBtnStyle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Content */}
      <div style={{
        flexGrow: 1,
        height: '100dvh',
        overflowY: 'hidden',
        padding: 7,
        boxSizing: 'border-box',
      }}>
        <PostSection
          post={post}
          isActive={true}
          inWindow={true}
          globalMuted={globalMuted}
          navigate={navigate}
          onLike={handleLike}
          onUnlike={handleUnlike}
          currentUserId={user?._id}
          onMusicClick={handleMusicClick}
        />
      </div>
    </div>
  );
}

const closeBtnStyle: React.CSSProperties = {
  position: 'fixed',
  top: 14,
  right: 18,
  zIndex: 60,
  width: 34,
  height: 34,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  borderRadius: 999,
  background:
    'radial-gradient(circle at 0% 0%, rgba(255,255,255,0.12), rgba(10,10,12,0.95))',
  color: '#e6e9f3',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  transition: 'transform 120ms ease, box-shadow 120ms ease',
  boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
};
